/* eslint-disable no-console */
import React, { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Cart, RecoveredCart, RecoveredCartItem, AddItemProps, CartType } from '@pegasus/cart';
import { useConfig } from '@browsing/config';
import { trackRemoveFromCart as triggerTrackRemoveFromCart } from '@browsing/tracking';

import {
    showHeaderSuccessFlashMessage,
    showHeaderErrorFlashMessage,
    showHeaderWarningFlashMessage,
} from '@client/redux/modules/flashMessage/actions';

import {
    setCart,
    resetCart,
    setRecoveredCart,
    setCartLoading,
    reserveCartStart,
    reserveCartSuccess,
    reserveCartFailure,
} from './redux/actions';
import { getCart, getRecoveredCart, getCartLoading } from './redux/selectors';
import * as cartServices from './services';
import { transformCartItems, manageAddToCartError } from './helpers';

interface CartActions {
    onItemRemove: (sku: string) => Promise<void>;
    onAllItemsRemove: () => Promise<void>;
    onItemReserveAgain: (props: AddItemProps & { successFlashMessage?: string }) => Promise<void>;
    onExtendCart: () => Promise<void>;
    onExpiredCart: () => void;
    onCartIconClick: () => void;
    onReserveCart: () => Promise<Cart>;
}

interface CompositeCart {
    cart: Cart;
    recoveredCart: RecoveredCart;
}

// we use a global variable to trigger
// the fetch of the cart on mount only once (promise deduplication)
let currentFetchPromise: Promise<any> | null = null;

/**
 * Cart functions hook.
 * This hook is good as long as we don't need
 * to share the state among different components
 * when needed, extract the state logic into context API or redux
 */
export const useCart = (): [CompositeCart, CartActions] => {
    const location = useLocation();
    const previousPathnameRef = React.useRef<string>(location.pathname);
    const config = useConfig();
    const cart = useSelector(getCart);
    const recoveredCart = useSelector(getRecoveredCart);
    const dispatch = useDispatch();

    const trackRemoveFromCart = useCallback(
        (simpleSkuRemoved: string) => {
            try {
                const cartItem = cart.items.find(({ simpleSku }) => simpleSku === simpleSkuRemoved);

                if (config.isEnabled('ga4')) {
                    triggerTrackRemoveFromCart(
                        {
                            item: cartItem,
                        },
                        config,
                    );
                }
            } catch (e) {
                console.error(e);
            }
        },
        [cart, config],
    );

    const fetchCart = useCallback(() => {
        // If there's already a fetch in progress, return that promise
        if (currentFetchPromise) {
            return currentFetchPromise;
        }

        dispatch(setCartLoading(true));

        currentFetchPromise = cartServices
            .getCart()
            .then(currCart => {
                if (currCart) {
                    dispatch(
                        setCart({
                            ...currCart,
                            items: transformCartItems(currCart.items, config),
                            ...(currCart.unavailableItems && {
                                unavailableItems: transformCartItems(
                                    currCart.unavailableItems,
                                    config,
                                ),
                            }),
                        }),
                    );
                }
            })
            .catch(console.error)
            .finally(() => {
                dispatch(setCartLoading(false));
                currentFetchPromise = null; // Reset for next fetch
            });

        return currentFetchPromise;
    }, [config, dispatch]);

    const fetchRecoveredCart = useCallback(async () => {
        try {
            // TODO: Temporarily disable the api call for the incomplete api, till it's fixed
            // https://jira.zalando.net/browse/ZLPC-2760
            // const recoveredCart = await cartServices.getRecoveredCart();
            const updatedRecoveredCart: RecoveredCart = { items: [] };

            dispatch(
                setRecoveredCart({
                    items: transformCartItems<RecoveredCartItem>(
                        updatedRecoveredCart?.items,
                        config,
                    ),
                }),
            );
        } catch (error) {
            console.error(error);
        }
    }, [config, dispatch]);

    const onItemRemove = useCallback(
        (itemSimpleSku: string) =>
            cartServices
                .deleteCartItem(itemSimpleSku)
                .then(() => {
                    trackRemoveFromCart(itemSimpleSku);

                    return fetchCart();
                })
                .catch(console.error),
        [fetchCart, trackRemoveFromCart],
    );

    const onAllItemsRemove = useCallback(
        () =>
            cartServices
                .deleteAllItemsFromCart()
                .then(() => {
                    return fetchCart();
                })
                .catch(console.error),
        [fetchCart],
    );

    const addItemToCart = useCallback((addToCartProps: AddItemProps) => {
        return cartServices
            .addItemToCart({
                ...addToCartProps,
            })
            .then(({ error }) => {
                if (error) {
                    return Promise.reject(manageAddToCartError(error));
                }

                return Promise.resolve();
            });
    }, []);

    const onItemReserveAgain: CartActions['onItemReserveAgain'] = useCallback(
        async ({ successFlashMessage = '', ...item }) => {
            try {
                await addItemToCart(item);
                await fetchCart();
                if (successFlashMessage) {
                    dispatch(showHeaderSuccessFlashMessage(successFlashMessage));
                }
            } catch (error) {
                if (error.errorMessage) {
                    dispatch(showHeaderErrorFlashMessage(error.errorMessage));
                } else if (error.warningMessage) {
                    dispatch(showHeaderWarningFlashMessage(error.warningMessage));
                }
            }
        },
        [addItemToCart, dispatch, fetchCart],
    );

    const onExtendCart = useCallback(
        () => cartServices.extendCart().then(fetchCart).catch(console.error),
        [fetchCart],
    );

    const onExpiredCart = useCallback(() => {
        if (cart?.cartType === CartType.RESERVED) {
            fetchCart();
        } else {
            dispatch(resetCart());
        }
    }, [dispatch, fetchCart, cart?.cartType]);

    // Initial fetch on mount
    useEffect(() => {
        fetchCart();
    }, [fetchCart]);

    // Route change fetch
    useEffect(() => {
        if (previousPathnameRef.current !== location.pathname) {
            fetchCart();
        }
        previousPathnameRef.current = location.pathname;
    }, [location.pathname, fetchCart]);

    useEffect(() => {
        fetchRecoveredCart();
    }, [fetchRecoveredCart]);

    const onCartIconClick = () => {
        try {
            fetchCart();
        } catch (error) {
            console.error(error);
        }
    };

    const onReserveCart = useCallback(async (): Promise<Cart> => {
        try {
            dispatch(reserveCartStart());

            const reservedCart = await cartServices.reserveCart();

            // Transform image URLs for both available and unavailable items
            const transformedReservedCart = {
                ...reservedCart,
                items: transformCartItems(reservedCart.items, config),
                ...(reservedCart.unavailableItems && {
                    unavailableItems: transformCartItems(reservedCart.unavailableItems, config),
                }),
            };

            dispatch(reserveCartSuccess(transformedReservedCart));

            return transformedReservedCart;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Failed to reserve cart items';
            console.error('Cart reservation failed:', error);

            dispatch(reserveCartFailure(errorMessage));

            throw error;
        }
    }, [config, dispatch]);

    return [
        { cart, recoveredCart },
        {
            onItemRemove,
            onAllItemsRemove,
            onItemReserveAgain,
            onExtendCart,
            onExpiredCart,
            onCartIconClick,
            onReserveCart,
        },
    ];
};
